// Typed client for the newsletter / mailing-list endpoints.
//
// The same editor UI drives two backends: the admin global tools
// (/api/admin/*, isAdmin-gated) and per-creator owner tools (/api/newsletter/me/*,
// mailer-gated). `createNewsletterService(opts)` produces an instance bound to a
// backend; components read the right instance from NewsletterServiceContext.
// System-email + media helpers are admin/shared only and stay standalone.

import { apiFetchJson, apiUpload } from './apiClient';
import { mediaIdFromSrc } from '@/utils/fileUtils';
import { FACTORY_PLAY_ONLY_MESSAGE, isFactoryRow } from '@/utils/factoryShader';
import type { NewsletterDoc } from '../../shared/newsletterBlocks';

// ── Shared types ─────────────────────────────────────────────────────────────

export interface SystemEmailTemplateData {
  templateId: string;
  label: string;
  tokens: { token: string; label: string }[];
  isCustom: boolean;
  updatedAt: string | null;
  doc: NewsletterDoc;
}

export interface CampaignSummary {
  id: number;
  subject: string;
  recipient_count: number;
  success_count: number;
  fail_count: number;
  started_at: string;
  finished_at: string | null;
  triggered_by: string | null;
  status: 'sent' | 'sending' | 'failed' | 'cancelled' | 'draft';
  list_id: number | null;
  list_name: string | null;
  opens_unique: number;
  opens_total: number;
  clicks_unique: number;
  clicks_total: number;
  clicks_human: number;
  unsubscribes: number;
  delivered_count: number;
  bounced_count: number;
  complained_count: number;
  has_template: number;
  // 1 = shown in the owner's profile "Email Highlights" row; 0 = hidden.
  show_in_highlights: number;
}

export interface ScheduledCampaign {
  id: number;
  subject: string;
  scheduled_at: string;
  status: 'scheduled' | 'failed' | 'cancelled';
  created_by: string | null;
  created_at: string;
  sent_campaign_id: number | null;
  error: string | null;
  list_id: number | null;
  list_name: string | null;
}

export interface CampaignDetail {
  id: number;
  subject: string;
  recipient_count: number;
  success_count: number;
  fail_count: number;
  started_at: string;
  finished_at: string | null;
  triggered_by: string | null;
  status: string;
  doc: NewsletterDoc | null;
  notes: string | null;
  opens: { unique_count: number; total: number; bot_count: number };
  clicks: { unique_count: number; total: number; bot_count: number };
  clicksByUrl: Array<{ url: string; total: number; human: number }>;
  unsubscribes: number;
  delivery: { delivered: number; bounced: number; complained: number };
}

export interface SubscriberStatsMonth {
  month: string; // 'YYYY-MM'
  added: number;
  unsubscribed: number;
  cumulative: number;
}

export interface SubscriberStats {
  months: SubscriberStatsMonth[];
  currentActive: number;
}

export interface Subscriber {
  kind: 'user' | 'external';
  id: number;
  email: string;
  display_name: string | null;
  subscribed: number;
  verified_at: string | null;
  source: string | null;
  imported_at: string | null;
  unsubscribed_at: string | null;
}

export interface Suppression {
  id: number;
  email: string;
  kind: 'bounce' | 'complaint';
  bounce_subtype: string | null;
  suppressed_at: string;
  message_id: string | null;
}

export interface MailingList {
  id: number | null; // null = synthetic "All subscribers" entry (admin only)
  name: string;
  description: string | null;
  member_count: number;
  created_by?: string | null;
  created_at?: string;
  owner_user_id?: number | null;
  builtin: boolean;
}

export interface MailingListMember {
  id: number;
  list_id: number;
  kind: 'user' | 'external' | 'raw';
  ref_id: number | null;
  email: string;
  display_name: string | null;
  added_at: string;
  added_by: string | null;
  unsubscribed_at: string | null;
  live_email?: string | null;
  live_subscribed?: number | null;
}

export interface ScheduledCampaignDetail {
  id: number;
  scheduled_at: string;
  status: string;
  created_by: string | null;
  list_id: number | null;
  doc: NewsletterDoc | null;
}

// ── System (transactional) email templates — admin only ──────────────────────

export async function getSystemEmailTemplate(templateId: string): Promise<SystemEmailTemplateData> {
  const res = await apiFetchJson<{ success: boolean } & SystemEmailTemplateData>(
    `/api/admin/system-emails/${templateId}`,
  );
  return {
    templateId: res.templateId,
    label: res.label,
    tokens: res.tokens,
    isCustom: res.isCustom,
    updatedAt: res.updatedAt,
    doc: res.doc,
  };
}

export async function saveSystemEmailTemplate(templateId: string, doc: NewsletterDoc): Promise<void> {
  await apiFetchJson<{ success: boolean }>(`/api/admin/system-emails/${templateId}`, {
    method: 'PUT',
    body: JSON.stringify({ doc }),
  });
}

export async function resetSystemEmailTemplate(templateId: string): Promise<void> {
  await apiFetchJson<{ success: boolean }>(`/api/admin/system-emails/${templateId}`, {
    method: 'DELETE',
  });
}

// ── Backend binding ──────────────────────────────────────────────────────────

// Per-org Reply-To override (owner surface only). From is always the
// platform's SES-verified address; `defaults` show what applies when the
// Reply-To field is unset. `defaults.reply_to` is the org owner's VERIFIED
// email (null when they have none: campaigns then carry no Reply-To and
// replies go to From). `is_owner` tells the UI whose account needs the email.
export interface SenderSettings {
  settings: { reply_to: string | null };
  defaults: { from_email: string | null; reply_to: string | null };
  effective: { sender_name: string | null; reply_to: string | null };
  is_owner: boolean;
}

export interface NewsletterServiceOpts {
  // Endpoint roots for the two backends.
  newsletterBase: string; // '/api/admin/newsletter' | '/api/newsletter/me'
  listsBase: string;      // '/api/admin/mailing-lists' | '/api/newsletter/me/mailing-lists'
  // Import differs: admin posts to /subscribers/import with listId in the body;
  // owner posts to a list-scoped import endpoint. Returns the request shape.
  buildImport: (
    rows: Array<{ email: string; display_name?: string | null }>,
    source: string,
    listId: number | null,
  ) => { url: string; body: unknown };
}

export function createNewsletterService(opts: NewsletterServiceOpts) {
  const { newsletterBase, listsBase } = opts;

  async function getNewsletterDraft(): Promise<{ doc: NewsletterDoc; updatedAt: string } | null> {
    const res = await apiFetchJson<{
      success: boolean;
      draft: { doc: NewsletterDoc; updatedAt: string } | null;
    }>(`${newsletterBase}/draft`);
    return res.draft;
  }

  async function saveNewsletterDraft(doc: NewsletterDoc): Promise<void> {
    await apiFetchJson<{ success: boolean }>(`${newsletterBase}/draft`, {
      method: 'PUT',
      body: JSON.stringify({ doc }),
    });
  }

  async function saveCampaign(doc: NewsletterDoc, campaignId?: number | null): Promise<{ id: number }> {
    if (campaignId != null) {
      await apiFetchJson<{ success: boolean }>(`${newsletterBase}/campaigns/${campaignId}`, {
        method: 'PUT',
        body: JSON.stringify({ doc }),
      });
      return { id: campaignId };
    }
    const res = await apiFetchJson<{ success: boolean; id: number }>(`${newsletterBase}/campaigns`, {
      method: 'POST',
      body: JSON.stringify({ doc }),
    });
    return { id: res.id };
  }

  async function deleteDraftCampaign(id: number): Promise<void> {
    await apiFetchJson<{ success: boolean }>(`${newsletterBase}/campaigns/${id}`, { method: 'DELETE' });
  }

  async function getSubscriberStats(months = 12): Promise<SubscriberStats> {
    const res = await apiFetchJson<{ success: boolean; stats: SubscriberStats }>(
      `${newsletterBase}/stats?months=${encodeURIComponent(months)}`,
    );
    return res.stats;
  }

  async function listCampaigns(): Promise<{ campaigns: CampaignSummary[]; scheduled: ScheduledCampaign[] }> {
    const res = await apiFetchJson<{
      success: boolean;
      campaigns: CampaignSummary[];
      scheduled: ScheduledCampaign[];
    }>(`${newsletterBase}/campaigns?limit=200`);
    return { campaigns: res.campaigns, scheduled: res.scheduled };
  }

  // Toggle a sent campaign's appearance in the owner's profile highlights.
  async function setCampaignHighlight(id: number, show: boolean): Promise<void> {
    await apiFetchJson<{ success: boolean }>(`${newsletterBase}/campaigns/${id}/highlight`, {
      method: 'PATCH',
      body: JSON.stringify({ show }),
    });
  }

  async function getCampaignDetail(id: number): Promise<CampaignDetail> {
    const res = await apiFetchJson<{ success: boolean; campaign: CampaignDetail }>(
      `${newsletterBase}/campaigns/${id}`,
    );
    return res.campaign;
  }

  async function duplicateCampaign(id: number): Promise<NewsletterDoc> {
    const res = await apiFetchJson<{ success: boolean; doc: NewsletterDoc }>(
      `${newsletterBase}/campaigns/${id}/duplicate`,
      { method: 'POST' },
    );
    return res.doc;
  }

  async function cancelScheduledCampaign(id: number): Promise<void> {
    await apiFetchJson<{ success: boolean }>(`${newsletterBase}/scheduled/${id}`, { method: 'DELETE' });
  }

  /** Permanently remove a schedule row that will never dispatch (cancelled /
   *  failed). The server refuses this for a still-pending send. */
  async function deleteScheduledCampaign(id: number): Promise<void> {
    await apiFetchJson<{ success: boolean }>(`${newsletterBase}/scheduled/${id}?purge=1`, {
      method: 'DELETE',
    });
  }

  async function getScheduledCampaign(id: number): Promise<ScheduledCampaignDetail> {
    const res = await apiFetchJson<{ success: boolean; scheduled: ScheduledCampaignDetail }>(
      `${newsletterBase}/scheduled/${id}`,
    );
    return res.scheduled;
  }

  // Subscriber browse + global unsubscribe + suppressions are admin-only surfaces
  // (no owner equivalent). They only get called in admin mode.
  async function listSubscribers(listOpts: {
    kind?: 'all' | 'user' | 'external';
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ subscribers: Subscriber[]; total: number }> {
    const params = new URLSearchParams();
    if (listOpts.kind) params.set('kind', listOpts.kind);
    if (listOpts.search) params.set('search', listOpts.search);
    if (listOpts.limit) params.set('limit', String(listOpts.limit));
    if (listOpts.offset) params.set('offset', String(listOpts.offset));
    const qs = params.toString();
    const res = await apiFetchJson<{ success: boolean; subscribers: Subscriber[]; total: number }>(
      `/api/admin/subscribers${qs ? `?${qs}` : ''}`,
    );
    return { subscribers: res.subscribers, total: res.total };
  }

  /**
   * Where an import into `listId` is posted and which body shape it takes (the C++ import feature builds the batches itself:
   * `withSource` = the admin body {rows, source, listId}; else the owner one {rows}). Derived from `buildImport`, the one place the
   * two endpoints are described.
   */
  function importTarget(listId: number | null): { path: string; withSource: boolean } {
    const { url, body } = opts.buildImport([], '', listId);
    return { path: url, withSource: typeof body === 'object' && body !== null && 'source' in body };
  }

  /** Where a campaign's report is read and its failed sends are retried (the C++ campaign-detail feature makes both requests). */
  function campaignTarget(id: number): { detailPath: string; retryPath: string } {
    return { detailPath: `${newsletterBase}/campaigns/${id}`, retryPath: `${newsletterBase}/campaigns/${id}/retry` };
  }

  /** Where the send and test-send dialogs' requests go (the C++ newsletter-send / email-test-send features make them). */
  function sendTarget(): { base: string; listsBase: string; testPath: string } {
    return { base: newsletterBase, listsBase, testPath: `${newsletterBase}/test-send` };
  }

  async function unsubscribeOne(kind: 'user' | 'external', id: number): Promise<void> {
    await apiFetchJson<{ success: boolean }>(`/api/admin/subscribers/${kind}/${id}/unsubscribe`, {
      method: 'POST',
    });
  }

  async function listSuppressions(): Promise<Suppression[]> {
    const res = await apiFetchJson<{ success: boolean; suppressions: Suppression[] }>(
      '/api/admin/newsletter/suppressions',
    );
    return res.suppressions;
  }

  async function listMailingLists(): Promise<MailingList[]> {
    const res = await apiFetchJson<{ success: boolean; lists: MailingList[] }>(listsBase);
    return res.lists;
  }

  async function createMailingList(name: string, description?: string): Promise<MailingList> {
    const res = await apiFetchJson<{ success: boolean; list: MailingList }>(listsBase, {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
    return res.list;
  }

  async function updateMailingList(
    id: number,
    patch: { name?: string; description?: string | null },
  ): Promise<MailingList> {
    const res = await apiFetchJson<{ success: boolean; list: MailingList }>(`${listsBase}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    return res.list;
  }

  async function deleteMailingList(id: number): Promise<void> {
    await apiFetchJson<{ success: boolean }>(`${listsBase}/${id}`, { method: 'DELETE' });
  }

  async function listMailingListMembers(
    listId: number,
    memberOpts: { search?: string; limit?: number; offset?: number } = {},
  ): Promise<{ members: MailingListMember[]; total: number }> {
    const params = new URLSearchParams();
    if (memberOpts.search) params.set('search', memberOpts.search);
    if (memberOpts.limit) params.set('limit', String(memberOpts.limit));
    if (memberOpts.offset) params.set('offset', String(memberOpts.offset));
    const qs = params.toString();
    const res = await apiFetchJson<{ success: boolean; members: MailingListMember[]; total: number }>(
      `${listsBase}/${listId}/members${qs ? `?${qs}` : ''}`,
    );
    return { members: res.members, total: res.total };
  }

  async function addMailingListMember(
    listId: number,
    member:
      | { kind: 'user' | 'external'; ref_id: number }
      | { kind: 'raw'; email: string; display_name?: string },
  ): Promise<MailingListMember> {
    const res = await apiFetchJson<{ success: boolean; member: MailingListMember }>(
      `${listsBase}/${listId}/members`,
      { method: 'POST', body: JSON.stringify(member) },
    );
    return res.member;
  }

  async function updateMailingListMember(
    listId: number,
    memberId: number,
    patch: { email?: string; display_name?: string | null },
  ): Promise<MailingListMember> {
    const res = await apiFetchJson<{ success: boolean; member: MailingListMember }>(
      `${listsBase}/${listId}/members/${memberId}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    );
    return res.member;
  }

  async function removeMailingListMember(listId: number, memberId: number): Promise<void> {
    await apiFetchJson<{ success: boolean }>(`${listsBase}/${listId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  async function getSenderSettings(): Promise<SenderSettings> {
    const res = await apiFetchJson<{ success: boolean } & SenderSettings>(
      `${newsletterBase}/sender-settings`,
    );
    return { settings: res.settings, defaults: res.defaults, effective: res.effective, is_owner: !!res.is_owner };
  }

  async function updateSenderSettings(replyTo: string | null): Promise<void> {
    await apiFetchJson<{ success: boolean }>(`${newsletterBase}/sender-settings`, {
      method: 'PUT',
      body: JSON.stringify({ reply_to: replyTo }),
    });
  }

  async function clearMailingListMembers(listId: number): Promise<number> {
    const res = await apiFetchJson<{ success: boolean; cleared: number }>(
      `${listsBase}/${listId}/clear`,
      { method: 'POST' },
    );
    return res.cleared;
  }

  return {
    getNewsletterDraft,
    saveNewsletterDraft,
    saveCampaign,
    deleteDraftCampaign,
    getSubscriberStats,
    listCampaigns,
    setCampaignHighlight,
    getCampaignDetail,
    duplicateCampaign,
    cancelScheduledCampaign,
    getScheduledCampaign,
    deleteScheduledCampaign,
    listSubscribers,
    importTarget,
    campaignTarget,
    sendTarget,
    unsubscribeOne,
    listSuppressions,
    listMailingLists,
    createMailingList,
    updateMailingList,
    deleteMailingList,
    listMailingListMembers,
    addMailingListMember,
    updateMailingListMember,
    removeMailingListMember,
    clearMailingListMembers,
    getSenderSettings,
    updateSenderSettings,
  };
}

export type NewsletterService = ReturnType<typeof createNewsletterService>;

// Admin global tools (/api/admin/*). Import here also posts to the global
// subscribers endpoint with the target list id in the body.
export const adminNewsletterService = createNewsletterService({
  newsletterBase: '/api/admin/newsletter',
  listsBase: '/api/admin/mailing-lists',
  buildImport: (rows, source, listId) => ({
    url: '/api/admin/subscribers/import',
    body: { rows, source, listId },
  }),
});

// Per-creator owner tools (/api/newsletter/me/*). Import is always list-scoped
// (the owner imports into a specific list of theirs).
export const myNewsletterService = createNewsletterService({
  newsletterBase: '/api/newsletter/me',
  listsBase: '/api/newsletter/me/mailing-lists',
  buildImport: (rows, _source, listId) => ({
    url: `/api/newsletter/me/mailing-lists/${listId}/import`,
    body: { rows },
  }),
});

// ── Media helpers (shared, admin + owner) ────────────────────────────────────

// Public origin that serves media to mail clients (unauthenticated GET).
const PUBLIC_MEDIA_ORIGIN = 'https://shadygram.com';

export async function uploadNewsletterImage(file: File): Promise<string> {
  const form = new FormData();
  form.append('media', file);
  form.append('isEncrypted', 'false');
  form.append('forceUnencrypted', 'true');
  form.append('folderKind', 'mail-campaigns');
  const res = await apiUpload('/api/media-uploads', form);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || `Upload failed (HTTP ${res.status})`);
  }
  const data = (await res.json()) as { url?: string };
  if (!data.url) throw new Error('Upload succeeded but no URL was returned.');
  const src = data.url.startsWith('http') ? data.url : `${PUBLIC_MEDIA_ORIGIN}${data.url}`;
  // Same fire-and-forget optimized-variant generation the library pick path
  // runs, so directly-uploaded email images get progressive editor previews too.
  const id = mediaIdFromSrc(src);
  if (id) triggerVariantGeneration({ id, mime_type: file.type, file_size: file.size }, src);
  return src;
}

export interface LibraryImageRef {
  id: string;
  mime_type?: string | null;
  original_filename?: string | null;
  is_encrypted?: boolean | number;
  plaintext_fek_present?: boolean | number;
  in_feed?: boolean | number;
  wrapped_fek?: string | null;
  previously_shared?: boolean | number;
  /** Read-only item shared by ANOTHER admin (public/shared pack overlay). We
   *  don't own it, so we can't publish it — but if it's already public we can
   *  stream it as-is. */
  shared_readonly?: boolean | number;
  /** A Factory shader (play-only) — never embedded anywhere. */
  factory?: boolean | number;
  /** Optional — present when `item` is actually a full `MediaUpload` row (the
   *  common case: every call site sources `item` from `beginLibraryPick()`,
   *  which resolves a `MediaUpload`). Used only to pass known caps to the
   *  lightweight-variant generator without re-probing them. */
  duration_seconds?: number | null;
  file_size?: number;
}

export interface ResolvedNewsletterImage {
  src: string;
  alt: string;
  published: boolean;
}

// What file kinds a block accepts. A picked item matches when its mime starts
// with any `mimePrefixes` entry OR its filename extension is in `extensions`
// (lower-case, no dot). Both empty/undefined = accept any file (the generic
// `file` picker). Extension matching exists because 3D models (.obj/.stl) carry
// no `model/*` mime — they're usually text/plain or application/octet-stream —
// so a mime-only gate would wrongly reject them.
interface AcceptSpec { mimePrefixes?: string[]; extensions?: string[] }

function fileExt(name?: string | null): string {
  const m = /\.([a-z0-9]+)$/i.exec(String(name || '').trim());
  return m ? m[1].toLowerCase() : '';
}

function itemMatchesAccept(item: LibraryImageRef, accept: AcceptSpec): boolean {
  const { mimePrefixes, extensions } = accept;
  if ((!mimePrefixes || !mimePrefixes.length) && (!extensions || !extensions.length)) return true;
  const mime = item.mime_type || '';
  if (mimePrefixes && mimePrefixes.some((p) => mime.startsWith(p))) return true;
  const ext = fileExt(item.original_filename);
  if (extensions && ext && extensions.includes(ext)) return true;
  return false;
}

// Resolve a picked Library item to a PUBLIC, streamable media URL — the SAME
// `/api/media-uploads/:id` endpoint the Library preview falls back to, which
// supports range requests (seeking). Shared by the image picker, the video
// block picker, and the library block's file picker; only the accepted kind
// differs.
//
// Whether a publish is needed is judged from the STOREFRONT's (anonymous)
// perspective, not the app's: the serve route only returns plaintext to an
// anonymous <img>/<video> when the file is unencrypted or published with a
// plaintext FEK (mediaUploadRoutes canServeFromB2 + its decrypt fallback).
// `in_feed` alone is NOT enough — an in-feed-but-unpublished encrypted file
// serves raw ciphertext to anonymous callers and renders blank on the site —
// so a feed-only file we own still gets published here. A read-only item
// shared by someone else can't be published by us, so if it isn't already
// public we surface a clear, actionable error instead of a failed owner-only
// publish (the old behavior, which broke picking pack videos).
// Fire-and-forget: generate + upload the lightweight homepage variants
// (960px WebP/JPEG image tier, 640px poster, re-encoded low-bitrate mp4) for
// an item that just got resolved to a public URL for a site-builder block.
// Dynamic-imported so the (ffmpeg.wasm-capable) module never lands in the
// main bundle for users who never touch the site builder. Detached — must
// never delay or affect `resolveLibraryMediaForBlock`'s own return value;
// `generateAndUploadVariantsForMedia` itself already swallows every internal
// error, but the `.catch` here is defense in depth against a chunk-load
// failure on the dynamic `import()` itself.
function triggerVariantGeneration(item: LibraryImageRef, src: string): void {
  const mime = item.mime_type || '';
  if (!mime.startsWith('image/') && !mime.startsWith('video/')) return;
  void import('@/services/variantGenerationService')
    .then((m) => m.generateAndUploadVariantsForMedia(item.id, mime, {
      sourceUrl: src,
      durationSeconds: item.duration_seconds ?? undefined,
      fileSize: item.file_size,
    }))
    .catch(() => { /* best-effort — never surfaces to the add-to-page flow */ });
}

async function resolveLibraryMediaForBlock(
  item: LibraryImageRef,
  accept: AcceptSpec,
  label: string,
  opts: { youtubeHint?: boolean } = {},
): Promise<ResolvedNewsletterImage> {
  if (!item?.id) throw new Error('Library item is missing an id.');
  if (isFactoryRow(item)) throw new Error(FACTORY_PLAY_ONLY_MESSAGE);
  if (!itemMatchesAccept(item, accept)) {
    throw new Error(`Only ${label} files can be placed in ${label} blocks.`);
  }
  const src = `${PUBLIC_MEDIA_ORIGIN}/api/media-uploads/${item.id}`;
  const alt = item.original_filename || '';

  // Already anonymously servable (unencrypted, or published with a plaintext
  // FEK — covers public/shared packs) → stream straight from the URL.
  if (!item.is_encrypted || item.plaintext_fek_present) {
    triggerVariantGeneration(item, src);
    return { src, alt, published: false };
  }
  // Private + not ours → we can't make it public; guide the user.
  if (item.shared_readonly) {
    throw new Error(
      `This ${label} is shared read-only and isn't public, so it can't be embedded on your site. ` +
      `Pick a ${label} you own${opts.youtubeHint ? ', or paste a YouTube link' : ''}.`,
    );
  }
  // Our own private upload → publish it (makes it public), then stream the URL.
  const { publishMediaUpload } = await import('@/services/mediaUploadService');
  await publishMediaUpload({
    id: item.id,
    is_encrypted: !!item.is_encrypted,
    wrapped_fek: item.wrapped_fek ?? null,
    previously_shared: !!item.previously_shared,
  });
  triggerVariantGeneration(item, src);
  return { src, alt, published: true };
}

export async function resolveLibraryImageForNewsletter(
  item: LibraryImageRef,
): Promise<ResolvedNewsletterImage> {
  return resolveLibraryMediaForBlock(item, { mimePrefixes: ['image/'] }, 'image');
}

export async function resolveLibraryVideoForBlock(
  item: LibraryImageRef,
): Promise<ResolvedNewsletterImage> {
  return resolveLibraryMediaForBlock(item, { mimePrefixes: ['video/'] }, 'video', { youtubeHint: true });
}

export interface ResolvedLibraryFile {
  src: string;
  filename: string;
  mime: string;
  published: boolean;
}

// Resolve any previewable library file for the library block's `file` mode. No
// kind gate (so .obj/.stl 3D models pass despite their non-`model/*` mime); the
// renderer dispatches on the returned filename/mime. Returns the public src plus
// the filename (ModelPreview needs it to tell .obj from .stl, and it's the
// download name) and mime.
export async function resolveLibraryFileForBlock(
  item: LibraryImageRef,
): Promise<ResolvedLibraryFile> {
  const r = await resolveLibraryMediaForBlock(item, {}, 'media');
  return {
    src: r.src,
    filename: item.original_filename || '',
    mime: item.mime_type || '',
    published: r.published,
  };
}

// Resolve a picked Library item to a public COVER image URL for a folder grid
// icon. Images use the image itself; a VIDEO (or any non-image with a generated
// thumbnail) falls back to the item's thumbnail instead of being rejected, so any
// media can serve as a folder cover. The item is published either way so the
// image/thumbnail serves to anonymous storefront visitors.
export async function resolveLibraryCoverForFolder(
  item: LibraryImageRef,
): Promise<{ src: string; published: boolean }> {
  if (!item?.id) throw new Error('Library item is missing an id.');
  if ((item.mime_type || '').startsWith('image/')) {
    const r = await resolveLibraryImageForNewsletter(item);
    return { src: r.src, published: r.published };
  }
  // Non-image: publish (no kind gate) so the /thumb endpoint serves the teaser
  // thumbnail publicly, then reuse the EXACT thumbnail URL the file's own card
  // renders — a same-origin RELATIVE `/api/media-uploads/:id/thumb`. (An absolute
  // PUBLIC_MEDIA_ORIGIN prefix can point at a different origin than the page and
  // render as a broken image; the relative path always resolves against whatever
  // origin is showing it — editor, storefront, or Library panel.)
  const r = await resolveLibraryMediaForBlock(item, {}, 'cover');
  return { src: `/api/media-uploads/${item.id}/thumb`, published: r.published };
}
